import { useState } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Icon from "@/v2-ui/icon"
import Button from "@/v2-backoffice-client/components/button"
import LayerAiForkCard from "@/v2-backoffice-client/layer/ai/LayerAiForkCard"
import LayerAiHistoryStepper from "@/v2-backoffice-client/layer/ai/LayerAiHistoryStepper"
import LayerAiDiffDialog from "@/v2-backoffice-client/layer/ai/LayerAiDiffDialog"
import { IS_CATALOG_AUTHORING_ENABLED } from "@/v2-backoffice-client/feature/feature.constants"
import type {
  LayerAiChatTurn,
  LayerAiMode,
} from "@/v2-backoffice-client/layer/ai/layer.ai.handoff"
import type {
  LayerAiAppliedStep,
  LayerAiFieldSuggestion,
  LayerAiForkProposal,
  LayerAiSuggestableField,
  LayerAiSuggestionStatus,
} from "@/v2-backoffice-client/layer/ai/layer.ai.types"

export type LayerAiSuggestionPanelProps = {
  suggestions: LayerAiFieldSuggestion[]
  /** The assistant's business summary of the current suggestion set. */
  changeSummary?: string | null
  /** The assistant's fork proposal, or the operator's manual one. */
  fork: LayerAiForkProposal | null
  /** Absent in create mode — forking needs an existing layer. */
  featureCode?: string
  layerName?: string
  /** Current Configuration editor draft — the diff base for that field. */
  draft: string
  /** Current saved blobs — the diff base for the other fields. */
  blobs: Partial<Record<LayerAiSuggestableField, unknown>>
  mode: LayerAiMode
  turns: LayerAiChatTurn[]
  isSavingField?: boolean
  /** Replaces the Configuration editor content. */
  onApply(configuration: string): void
  /** Persists an accepted non-Configuration suggestion. */
  onSaveField(field: LayerAiSuggestableField, value: unknown): Promise<unknown>
  /** "To a fork…": proposes a manual fork (owned by the conversation host). */
  onRequestFork(proposal: LayerAiForkProposal): void
  onDismissFork(): void
  /** A fork was created and the sheet is navigating to it. */
  onForked(): void
  /**
   * The history replayed values (rewind/re-apply): the host refreshes
   * anything that mirrors the documents, e.g. the preview map.
   */
  onHistoryNavigated?(): void
}

/**
 * Everything below the thread: the fork card, the suggestion list with
 * per-field apply/dismiss/diff, and the application history stepper. Owns
 * the review state (statuses, open diff, applied steps); landing a value
 * goes through the host's `onApply`/`onSaveField`.
 */
function LayerAiSuggestionPanel(props: LayerAiSuggestionPanelProps) {
  const {
    suggestions,
    changeSummary,
    fork,
    featureCode,
    layerName,
    draft,
    blobs,
    mode,
    turns,
    isSavingField,
    onApply,
    onSaveField,
    onRequestFork,
    onDismissFork,
    onForked,
    onHistoryNavigated,
  } = props

  const [suggestionStatus, setSuggestionStatus] = useState<
    Record<string, LayerAiSuggestionStatus>
  >({})
  const [diffField, setDiffField] = useState<string | null>(null)
  // The suggestions box stays listed after acting (fork/restore need it)
  // but can be tucked away once everything is applied — a collapsed chip
  // brings it back.
  const [isCollapsed, setIsCollapsed] = useState(false)
  // Application history: every accepted suggestion is a step; `stepCursor`
  // is how many of them are currently in effect. Survives suggestion-set
  // replacements — the values were already landed.
  const [steps, setSteps] = useState<LayerAiAppliedStep[]>([])
  const [stepCursor, setStepCursor] = useState(0)

  // A new suggestion-carrying turn replaces the set: reset the review state
  // (the React "adjust state during render" pattern — no effect needed).
  const [prevSuggestions, setPrevSuggestions] = useState(suggestions)
  if (suggestions !== prevSuggestions) {
    setPrevSuggestions(suggestions)
    setSuggestionStatus({})
    setDiffField(null)
    setIsCollapsed(false)
  }

  /** The blob the diff compares against: editor draft for Configuration, the saved value for the rest. */
  function getCurrentValue(field: string) {
    if (field === "Configuration") return draft
    const value = blobs[field as LayerAiSuggestableField]
    return value == null ? "null" : JSON.stringify(value, null, 2)
  }

  function setStatus(field: string, status: LayerAiSuggestionStatus) {
    setSuggestionStatus((current) => ({ ...current, [field]: status }))
  }

  function dismiss(field: string) {
    setStatus(field, "dismissed")
    if (diffField === field) setDiffField(null)
  }

  /** Lands a value on its destination: editor draft or a direct save. */
  async function landValue(field: string, value: string) {
    if (field === "Configuration") {
      onApply(value)
      return
    }
    await onSaveField(field as LayerAiSuggestableField, JSON.parse(value))
  }

  async function apply(suggestion: LayerAiFieldSuggestion) {
    // Captured BEFORE landing, so a step-back restores what the operator
    // actually had at this point — including their own manual edits.
    const before = getCurrentValue(suggestion.Field)
    await landValue(suggestion.Field, suggestion.Value)
    // Applying after stepping back discards the undone tail, like any
    // editor's redo history would.
    setSteps([
      ...steps.slice(0, stepCursor),
      {
        changes: [{ field: suggestion.Field, before, after: suggestion.Value }],
        summary: changeSummary
          ? `${changeSummary} (${suggestion.Field})`
          : undefined,
      },
    ])
    setStepCursor(stepCursor + 1)
    setStatus(suggestion.Field, "applied")
    if (diffField === suggestion.Field) setDiffField(null)
  }

  /** Lands every pending suggestion as ONE rewindable history entry. */
  async function applyAll() {
    const pending = suggestions.filter(
      (suggestion) =>
        (suggestionStatus[suggestion.Field] ?? "pending") === "pending",
    )
    if (pending.length === 0) return

    const changes = []
    for (const suggestion of pending) {
      const before = getCurrentValue(suggestion.Field)
      await landValue(suggestion.Field, suggestion.Value)
      changes.push({
        field: suggestion.Field,
        before,
        after: suggestion.Value,
      })
    }

    setSteps([
      ...steps.slice(0, stepCursor),
      { changes, summary: changeSummary ?? undefined },
    ])
    setStepCursor(stepCursor + 1)
    setSuggestionStatus((current) => {
      const next = { ...current }
      for (const suggestion of pending) next[suggestion.Field] = "applied"
      return next
    })
    setDiffField(null)
  }

  /**
   * Walks the application history to `target` applied steps, replaying
   * each step's recorded before/after values — an absolute jump, so it
   * re-syncs the editor even after manual undos in between. Batch steps
   * rewind their changes in reverse application order.
   */
  async function goToStep(target: number) {
    if (isSavingField) return
    let cursor = stepCursor
    while (cursor > target) {
      cursor -= 1
      for (const change of [...steps[cursor].changes].reverse()) {
        await landValue(change.field, change.before)
      }
    }
    while (cursor < target) {
      for (const change of steps[cursor].changes) {
        await landValue(change.field, change.after)
      }
      cursor += 1
    }
    setStepCursor(cursor)
    // The documents just jumped in time — let the host re-sync mirrors
    // (the preview map applies the navigated-to draft immediately).
    onHistoryNavigated?.()
  }

  const activeSuggestions = suggestions.filter(
    (suggestion) => suggestionStatus[suggestion.Field] !== "dismissed",
  )
  const pendingCount = suggestions.filter(
    (suggestion) =>
      (suggestionStatus[suggestion.Field] ?? "pending") === "pending",
  ).length
  const hasPendingConfiguration = activeSuggestions.some(
    (suggestion) =>
      suggestion.Field === "Configuration" &&
      (suggestionStatus[suggestion.Field] ?? "pending") === "pending",
  )

  return (
    <>
      {fork && featureCode && (
        <LayerAiForkCard
          // Remount on a new proposal so the editable copy re-seeds.
          key={`${fork.FeatureCode}\u0000${fork.Name}`}
          proposal={fork}
          featureCode={featureCode}
          draft={draft}
          activeSuggestions={activeSuggestions}
          hasPendingConfiguration={hasPendingConfiguration}
          mode={mode}
          turns={turns}
          onDismiss={onDismissFork}
          onForked={onForked}
        />
      )}

      {suggestions.length > 0 && isCollapsed && (
        <Button
          variant="ghost"
          size="tiny"
          iconSrc="sparkles"
          className="w-full !justify-start border bg-muted/30 font-normal text-muted-foreground"
          data-feature-code="UI_BackofficeLayers_AiAssistant_SuggestionsExpand"
          onClick={() => setIsCollapsed(false)}
        >
          {suggestions.length}{" "}
          {suggestions.length === 1 ? "suggestion" : "suggestions"}
          {" — "}
          {
            suggestions.filter(
              (suggestion) => suggestionStatus[suggestion.Field] === "applied",
            ).length
          }{" "}
          applied
          <span className="flex-1" />
          {/* Up = "expands upward" (the box sits above the composer). */}
          <Icon src="angle-small-up" size={10} />
        </Button>
      )}

      {suggestions.length > 0 && !isCollapsed && (
        <div className="flex shrink-0 flex-col gap-0.5 rounded-md border bg-muted/30 px-2 py-1.5">
          <p className="flex items-center gap-1.5 text-xs font-medium">
            <Icon src="sparkles" size={12} className="text-primary" />
            Suggestions
            <span className="truncate font-normal text-muted-foreground">
              {changeSummary
                ? `— ${changeSummary}`
                : "— review before applying"}
            </span>
            <span className="flex-1" />
            {pendingCount > 1 && (
              <Button
                size="tiny"
                variant="outline"
                iconSrc="check"
                isLoading={isSavingField}
                title="Apply every pending suggestion (one history entry)"
                data-feature-code="UI_BackofficeLayers_AiAssistant_ApplyAll"
                onClick={() => void applyAll()}
              >
                Accept all
              </Button>
            )}
            {IS_CATALOG_AUTHORING_ENABLED && !fork && featureCode && (
              <Button
                size="tiny"
                variant="ghost"
                iconSrc="code-fork"
                className="font-normal"
                title="Land these suggestions on a fork of this layer instead of changing it"
                data-feature-code="UI_BackofficeLayers_AiAssistant_ToFork"
                onClick={() =>
                  onRequestFork({
                    FeatureCode: `${featureCode}_Fork`,
                    Name: layerName ? `${layerName} (fork)` : "",
                  })
                }
              >
                To a fork…
              </Button>
            )}
            <Button
              size="tiny"
              variant="ghost"
              iconSrc="angle-small-down"
              title="Collapse the suggestions (a chip brings them back)"
              aria-label="Collapse the suggestions"
              data-feature-code="UI_BackofficeLayers_AiAssistant_SuggestionsCollapse"
              onClick={() => setIsCollapsed(true)}
            />
          </p>
          {suggestions.map((suggestion) => {
            const status = suggestionStatus[suggestion.Field] ?? "pending"
            return (
              <div key={suggestion.Field} className="flex items-center gap-1.5">
                <span
                  className={cn(
                    "flex-1 truncate text-xs",
                    status === "dismissed" &&
                      "text-muted-foreground line-through",
                  )}
                >
                  {suggestion.Field}
                </span>

                {status === "applied" && (
                  <span className="flex items-center gap-1 text-[11px] text-green-500">
                    <Icon src="check-circle" size={11} />
                    Applied
                  </span>
                )}

                {status === "dismissed" ? (
                  <Button
                    size="tiny"
                    variant="ghost"
                    iconSrc="undo-alt"
                    title="Bring this suggestion back"
                    data-feature-code="UI_BackofficeLayers_AiAssistant_Restore"
                    onClick={() => setStatus(suggestion.Field, "pending")}
                  >
                    Restore
                  </Button>
                ) : (
                  <>
                    <Button
                      size="tiny"
                      variant="outline"
                      data-feature-code="UI_BackofficeLayers_AiAssistant_ViewDiff"
                      onClick={() => setDiffField(suggestion.Field)}
                    >
                      Review diff
                    </Button>
                    {status === "pending" && (
                      <Button
                        size="tiny"
                        variant="ghost"
                        iconSrc="check"
                        isLoading={isSavingField}
                        title={
                          suggestion.Field === "Configuration"
                            ? "Apply to the editor draft"
                            : featureCode
                              ? "Save to the layer"
                              : "Attach to the new layer"
                        }
                        aria-label="Apply suggestion"
                        data-feature-code="UI_BackofficeLayers_AiAssistant_Apply"
                        onClick={() => void apply(suggestion)}
                      />
                    )}
                    <Button
                      size="tiny"
                      variant="ghost"
                      iconSrc="cross-small"
                      title={
                        status === "applied"
                          ? "Exclude from a fork (the applied value stays; step back in the history to revert it)"
                          : "Dismiss this suggestion"
                      }
                      aria-label="Dismiss suggestion"
                      data-feature-code="UI_BackofficeLayers_AiAssistant_Dismiss"
                      onClick={() => dismiss(suggestion.Field)}
                    />
                  </>
                )}
              </div>
            )
          })}
        </div>
      )}

      {steps.length > 0 && (
        <LayerAiHistoryStepper
          steps={steps}
          cursor={stepCursor}
          isBusy={Boolean(isSavingField)}
          onGoToStep={(target) => void goToStep(target)}
        />
      )}

      <LayerAiDiffDialog
        field={diffField}
        suggestions={suggestions}
        getCurrentValue={getCurrentValue}
        isSaving={isSavingField}
        onSelectField={setDiffField}
        onApply={(suggestion) => void apply(suggestion)}
        onClose={() => setDiffField(null)}
      />
    </>
  )
}

export default LayerAiSuggestionPanel
